"use client";

// MintModal — naming a connection, and wiring it.
//
// The connect window used to be about a BRAND: "connect Claude Code", one row,
// one endpoint, and no way to tell your laptop from the studio machine. This
// window is about a RELATIONSHIP. You name it first — the name is prefilled but
// yours — and the connection you mint gets its own door: the same MCP endpoint
// carrying that connection's tag, so when a call arrives innernet knows which of
// your Claude Codes is speaking.
//
// Built on the shared StepShell, so it reads as the same stepped window as the
// rest of the connections model: name → wire → self-start → watch.

import { useCallback, useEffect, useMemo, useState } from "react";
import BrandAvatar from "../BrandAvatar";
import CopyDisc from "../CopyDisc";
import StepShell, { type FlowStep } from "./StepShell";
import { Field } from "../ui";
import { MCP_URL, PROMPT_SYNC, type ConnectTool } from "../../lib/connect/tools";
import {
  connState,
  connectionUrl,
  stateLine,
  suggestLabel,
  type Connection,
} from "../../lib/connect/connections";
import { relTime } from "../../lib/os/tree";
import { trackFunnel, EVENTS } from "../../lib/analytics";

function CopyBlock({ text, mono = true }: { text: string; mono?: boolean }) {
  // THE copy disc in the block's corner (components/CopyDisc)
  return (
    <div className="cm-block">
      <pre style={{ fontFamily: mono ? "var(--font-mono)" : "inherit" }}>{text}</pre>
      <CopyDisc text={text} className="cm-block-take" onCopied={() => trackFunnel(EVENTS.connect_instructions_copied, { what: "connection_endpoint" })} />
    </div>
  );
}

/** the presence mark — a dot whose fill IS the state. Never a green light: an
 *  active connection is one that read your memory lately, and that is all we can
 *  honestly claim. */
export function StateDot({ state }: { state: ReturnType<typeof connState> }) {
  return <span className="conn-dot" data-state={state} aria-hidden />;
}

export default function MintModal({
  tool,
  connections,
  accountName,
  onClose,
  onChanged,
}: {
  tool: ConnectTool;
  /** every connection already minted for THIS tool. */
  connections: Connection[];
  /** the signed-in person, for the name we offer. */
  accountName?: string | null;
  onClose: () => void;
  /** a connection was minted, renamed, or let go — refetch upstream. */
  onChanged: () => void;
}) {
  const [step, setStep] = useState(0);
  const [label, setLabel] = useState(() => suggestLabel(tool.id, accountName, connections));
  const [minted, setMinted] = useState<Connection | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // the live one we're teaching: the freshly minted row, else the tool's newest.
  const subject = minted ?? null;
  const endpoint = subject ? connectionUrl(subject.token) : MCP_URL;

  // the tool's own snippet, but pointed at THIS connection's door. Every entry in
  // the catalogue embeds the bare endpoint, so swapping it through is enough.
  const config = useMemo(
    () => tool.config.split(MCP_URL).join(endpoint),
    [tool.config, endpoint],
  );

  // the snippet belongs under the step that ASKS for it — "paste this url",
  // "add the block below", "run the command". It used to sit under step 1
  // regardless, so Claude Desktop showed you something to copy while the step
  // above it still said "open Settings".
  const configStep = useMemo(() => {
    const i = tool.steps.findIndex((s) => /paste|below|run the command|add the block/i.test(s));
    return i >= 0 ? i : 0;
  }, [tool.steps]);

  const mint = useCallback(async () => {
    const name = label.trim();
    if (!name) { setError("give it a name — anything you'll recognise"); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/connections", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tool_id: tool.id, label: name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error ?? "could not mint that");
      setMinted(data.connection as Connection);
      onChanged();
      // minting is the moment intent becomes a named relationship — the step the
      // funnel most needs to see, and `index` orders it against the wiring beats.
      trackFunnel(EVENTS.onboarding_step_completed, {
        step: `mint.${tool.id}`, index: 0, flow: "connect_mint", client: tool.id,
      });
      setStep(1);
    } catch (e) {
      const reason = e instanceof Error ? e.message : "could not mint that";
      setError(reason);
      // The half of this flow that never had a number. connect_failed has been
      // in the taxonomy since it was written and fired from nowhere, so a mint
      // that broke looked identical to a mint nobody attempted.
      trackFunnel(EVENTS.connect_failed, { client: tool.id, reason });
    } finally {
      setBusy(false);
    }
  }, [label, tool.id, onChanged]);

  // ── the watch ──────────────────────────────────────────────────────────────
  // Poll this connection until it is seen. Not a spinner pretending to know:
  // it is literally waiting for the tool to make its first call.
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!subject || step < 3 || seen) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async () => {
      try {
        const res = await fetch("/api/connections", { cache: "no-store" });
        if (res.ok && !disposed) {
          const data = (await res.json()) as { connections?: Connection[] };
          const mine = (data.connections ?? []).find((c) => c.id === subject.id);
          if (mine?.last_seen_at) {
            setSeen(true);
            setMinted(mine);
            onChanged();
            // THE activation moment: not "they copied the config" but "their
            // tool actually called us". It is the truest signal the product
            // has and it was the one thing here going unrecorded.
            trackFunnel(EVENTS.connect_verified, { client: tool.id, via: "mint_poll" });
            window.dispatchEvent(new CustomEvent("innernet:connected"));
            return;
          }
        }
      } catch { /* offline — keep waiting */ }
      if (!disposed) timer = setTimeout(poll, 3000);
    };
    void poll();
    return () => { disposed = true; if (timer) clearTimeout(timer); };
  }, [subject, step, seen, onChanged]);

  const steps: FlowStep[] = [
    {
      key: "name",
      title: minted ? "named" : `name this ${tool.generic ? "connection" : tool.name}`,
      body: (
        <>
          <p className="cm-sub">
            every connection is its own relationship. name it so you can tell this one from the
            next — the laptop from the studio machine, work from home.
          </p>
          <Field
            label="what to call it"
            value={label}
            autoFocus
            maxLength={80}
            error={error}
            disabled={!!minted}
            onChange={(e) => { setLabel(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !minted) void mint(); }}
          />
          {connections.length > 0 && (
            <div style={{ marginTop: "1.6rem" }}>
              <span className="field-label">already here</span>
              <div className="conn-mini">
                {connections.map((c) => {
                  const st = connState(c);
                  return (
                    <div key={c.id} className="conn-mini-row">
                      <StateDot state={st} />
                      <span className="conn-mini-name">{c.label}</span>
                      <span className="conn-mini-state">
                        {stateLine(st, c.last_seen_at ? relTime(c.last_seen_at) : null)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ),
      primary: minted
        ? { label: "wire it up", onClick: () => setStep(1), done: true }
        : { label: "mint this connection", onClick: () => void mint(), loading: busy },
    },
    {
      key: "wire",
      title: `wire ${tool.generic ? "it" : tool.name}`,
      body: (
        <>
          <p className="cm-sub">
            this url belongs to <strong style={{ color: "var(--ink)" }}>{minted?.label ?? label}</strong> alone.
            paste it into that one tool and nowhere else — it is how innernet knows which of your
            tools is speaking. it is not a key: signing in still happens in your browser.
          </p>
          <ol className="cm-steps">
            {tool.steps.map((s, i) => (
              <li key={i}>
                <span className="cm-step-n">{i + 1}</span>
                <span className="cm-step-body">
                  {s.split(MCP_URL).join(endpoint)}
                  {i === configStep && (
                    <CopyBlock text={config} mono={!tool.configLabel.includes("url")} />
                  )}
                </span>
              </li>
            ))}
          </ol>
        </>
      ),
      primary: { label: "next", onClick: () => setStep(2) },
    },
    {
      key: "selfstart",
      title: "make it self-start",
      body: (
        <>
          <p className="cm-sub">
            paste this once as a standing instruction. from then on it loads who you are and
            quietly records what changes.
          </p>
          <CopyBlock text={PROMPT_SYNC} mono={false} />
          <p className="cm-where"><span aria-hidden>↳</span> in <strong>{tool.name}</strong>: {tool.promptWhere}.</p>
        </>
      ),
      primary: { label: "next", onClick: () => setStep(3) },
    },
    {
      key: "watch",
      title: seen ? "it spoke" : "watching for it",
      body: (
        <>
          <div className="cm-verify">
            <span className="cm-verify-dot" data-pulse={!seen || undefined} data-on={seen || undefined} aria-hidden />
            <div className="cm-verify-body">
              <div className="cm-verify-head" data-on={seen || undefined}>
                {seen ? `${minted?.label ?? "it"} is reading your memory` : "nothing yet"}
              </div>
              <p className="cm-sub" style={{ margin: 0 }}>
                {seen
                  ? "this connection is active. it will stay that way as long as it keeps reading — you'll see the date here either way."
                  : `say hi to ${tool.name}. the moment it calls through this connection's url, it lands here with a date on it.`}
              </p>
            </div>
          </div>
          <button type="button" className="im-quiet" onClick={onClose} style={{ marginTop: "1.4rem" }}>
            i&apos;ll check back later
          </button>
        </>
      ),
      primary: { label: seen ? "done" : "close", onClick: onClose, done: seen },
    },
  ];

  return (
    <StepShell
      ariaLabel={`connect ${tool.name}`}
      head={<>
        <BrandAvatar name={tool.icon_key} size={34} />
        <span className="cm-name">{minted?.label ?? tool.name}</span>
        {minted && <span className="cm-rec cm-rec--live"><StateDot state={connState(minted)} />minted</span>}
      </>}
      steps={steps}
      step={step}
      setStep={setStep}
      onClose={onClose}
    />
  );
}
